import React, { useState, useEffect, useCallback } from 'react';
import { getSessionUUID } from './lib/AuthToken';
import { useAuthProvider } from '../providers/AuthProvider';

// Split text into plain and URL segments so links can be rendered as <a> tags
const renderWithLinks = (text, linkColor) => {
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, idx) =>
        /^https?:\/\//.test(part) ? (
            <a key={idx} href={part} target="_blank" rel="noopener noreferrer"
               style={{ color: linkColor || '#1997eb', wordBreak: 'break-all' }}>
                {part}
            </a>
        ) : part
    );
};

const MAX_IMAGES = 3;

const NoticeBoard = ({ theme, isAdmin: propIsAdmin }) => {
    const { permissions, isDev, isAdmin: authIsAdmin } = useAuthProvider();
    const isAdmin = propIsAdmin !== undefined ? propIsAdmin : authIsAdmin;
    const canEdit = isDev || (permissions && permissions.perm_notice_board_edit);
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingNotice, setEditingNotice] = useState(null); // null = new notice

    // Form state
    const [formTitle, setFormTitle] = useState('');
    const [formContent, setFormContent] = useState('');
    const [formImages, setFormImages] = useState([]); // [{ path, url }]
    const [formActive, setFormActive] = useState(true);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Image uploading
    const [imageUploading, setImageUploading] = useState(false);

    // Lightbox
    const [lightboxSrc, setLightboxSrc] = useState(null);

    // ------------------------------------------------------------------
    // Fetch notices
    // ------------------------------------------------------------------
    const fetchNotices = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const sessionToken = getSessionUUID();
            const qs = isAdmin ? '?include_inactive=true' : '';
            const res = await fetch(`/notice_board/notices${qs}`, {
                credentials: 'include',
                headers: { 'Authorization': `Bearer ${sessionToken}` },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to fetch notices');
            setNotices(data.notices || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);

    // ------------------------------------------------------------------
    // Open modal helpers
    // ------------------------------------------------------------------
    const openNewNotice = () => {
        setEditingNotice(null);
        setFormTitle('');
        setFormContent('');
        setFormImages([]);
        setFormActive(true);
        setFormError('');
        setShowModal(true);
    };

    const openEditNotice = (notice) => {
        setEditingNotice(notice);
        setFormTitle(notice.title);
        setFormContent(notice.content);
        setFormImages(notice.image_urls || []);
        setFormActive(notice.is_active);
        setFormError('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingNotice(null);
        setFormError('');
    };

    // ------------------------------------------------------------------
    // Image upload
    // ------------------------------------------------------------------
    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        if (formImages.length + files.length > MAX_IMAGES) {
            setFormError(`You can attach a maximum of ${MAX_IMAGES} images per notice.`);
            return;
        }

        setImageUploading(true);
        setFormError('');

        try {
            const sessionToken = getSessionUUID();
            for (const file of files) {
                const formData = new FormData();
                formData.append('image', file);

                const res = await fetch('/notice_board/upload_image', {
                    method: 'POST',
                    credentials: 'include',
                    headers: { 'Authorization': `Bearer ${sessionToken}` },
                    body: formData,
                });
                const data = await res.json();
                if (!res.ok) throw new Error(data.error || 'Image upload failed');

                setFormImages((prev) => [
                    ...prev,
                    { path: data.image_path, url: data.image_url },
                ]);
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setImageUploading(false);
            // Reset file input
            e.target.value = '';
        }
    };

    const handleRemoveImage = async (index) => {
        const img = formImages[index];
        // Remove from storage
        try {
            const sessionToken = getSessionUUID();
            await fetch('/notice_board/delete_image', {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`,
                },
                body: JSON.stringify({ image_path: img.path }),
            });
        } catch {
            // Non-critical — continue
        }
        setFormImages((prev) => prev.filter((_, i) => i !== index));
    };

    // ------------------------------------------------------------------
    // Submit (create / update)
    // ------------------------------------------------------------------
    const handleSubmit = async () => {
        if (!formTitle.trim()) { setFormError('Title is required'); return; }
        if (!formContent.trim()) { setFormError('Content is required'); return; }

        setFormSubmitting(true);
        setFormError('');

        const imagePaths = formImages.map((img) => img.path);
        const sessionToken = getSessionUUID();

        try {
            let res, data;

            if (editingNotice) {
                res = await fetch(`/notice_board/update/${editingNotice.id}`, {
                    method: 'PUT',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`,
                    },
                    body: JSON.stringify({
                        title: formTitle,
                        content: formContent,
                        image_paths: imagePaths,
                        is_active: formActive,
                    }),
                });
            } else {
                res = await fetch('/notice_board/create', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${sessionToken}`,
                    },
                    body: JSON.stringify({
                        title: formTitle,
                        content: formContent,
                        image_paths: imagePaths,
                    }),
                });
            }

            data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Failed to save notice');

            closeModal();
            fetchNotices();
        } catch (err) {
            setFormError(err.message);
        } finally {
            setFormSubmitting(false);
        }
    };

    // ------------------------------------------------------------------
    // Delete notice
    // ------------------------------------------------------------------
    const handleDelete = async (noticeId) => {
        if (!window.confirm('Are you sure you want to permanently delete this notice?')) return;
        try {
            const sessionToken = getSessionUUID();
            const res = await fetch(`/notice_board/delete/${noticeId}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: { 'Authorization': `Bearer ${sessionToken}` },
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Delete failed');
            fetchNotices();
        } catch (err) {
            alert(err.message);
        }
    };

    // ------------------------------------------------------------------
    // Styles derived from theme
    // ------------------------------------------------------------------
    const isDark = theme === 'dark';
    const card = {
        background: isDark ? '#333' : '#fff',
        border: `1px solid ${isDark ? '#555' : '#e0e0e0'}`,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.08)',
    };
    const textColor = isDark ? '#fff' : '#333';
    const subText = isDark ? '#bbb' : '#666';
    const inputStyle = {
        width: '100%',
        padding: '10px 12px',
        borderRadius: '6px',
        border: `1px solid ${isDark ? '#555' : '#ccc'}`,
        background: isDark ? '#444' : '#fff',
        color: textColor,
        marginBottom: '12px',
        fontSize: '14px',
        boxSizing: 'border-box',
    };
    const labelStyle = {
        display: 'block',
        marginBottom: '6px',
        fontWeight: '600',
        color: textColor,
        fontSize: '14px',
    };
    const btnPrimary = {
        padding: '8px 18px',
        backgroundColor: '#1997eb',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
    };
    const btnDanger = {
        padding: '8px 14px',
        backgroundColor: '#dc3545',
        color: '#fff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13px',
    };
    const btnSecondary = {
        padding: '8px 14px',
        backgroundColor: isDark ? '#555' : '#e0e0e0',
        color: textColor,
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        fontSize: '13px',
    };

    // ------------------------------------------------------------------
    // Render
    // ------------------------------------------------------------------
    return (
        <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
            {/* Header */}
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <h2 style={{ color: textColor, marginBottom: '6px' }}>📋 Notice Board</h2>
                <p style={{ color: subText, fontSize: '14px', margin: '0 0 16px 0' }}>
                    Latest notices for drivers — up to 5 most recent shown.
                </p>
                {canEdit && (
                    <button style={btnPrimary} onClick={openNewNotice}>+ Post Notice</button>
                )}
            </div>

            {/* Error banner */}
            {error && (
                <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', padding: '12px 16px', borderRadius: '8px', color: '#721c24', marginBottom: '20px' }}>
                    {error}
                </div>
            )}

            {/* Loading */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '60px', color: subText }}>
                    Loading notices...
                </div>
            )}

            {/* Empty state */}
            {!loading && !error && notices.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px', color: subText }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                    <p style={{ fontSize: '18px', margin: 0 }}>No notices yet.</p>
                    {canEdit && (
                        <p style={{ fontSize: '14px', marginTop: '8px' }}>
                            Click <strong>Post Notice</strong> above to create the first one.
                        </p>
                    )}
                </div>
            )}

            {/* Notice cards */}
            {!loading && notices.map((notice) => (
                <div key={notice.id} style={{
                    ...card,
                    borderLeft: `4px solid ${notice.is_active ? '#1997eb' : '#999'}`,
                    opacity: notice.is_active ? 1 : 0.65,
                }}>
                    {/* Title row */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <h3 style={{ color: textColor, margin: '0 0 4px 0', fontSize: '18px' }}>
                                {notice.title}
                                {!notice.is_active && (
                                    <span style={{ marginLeft: '10px', fontSize: '12px', color: '#999', fontWeight: 'normal' }}>(inactive)</span>
                                )}
                            </h3>
                            <div style={{ fontSize: '12px', color: subText }}>
                                Posted by <strong>{notice.creator_name}</strong>
                                {' · '}
                                {new Date(notice.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                {notice.modifier_name && notice.updated_at !== notice.created_at && (
                                    <span>
                                        {' · Edited by '}<strong>{notice.modifier_name}</strong>
                                        {' '}
                                        {new Date(notice.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                    </span>
                                )}
                            </div>
                        </div>
                        {canEdit && (
                            <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                                <button style={{ ...btnSecondary, fontSize: '12px' }} onClick={() => openEditNotice(notice)}>
                                    Edit
                                </button>
                                <button style={{ ...btnDanger, fontSize: '12px' }} onClick={() => handleDelete(notice.id)}>
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <p style={{ color: textColor, lineHeight: '1.6', margin: '0 0 12px 0', whiteSpace: 'pre-wrap' }}>
                        {renderWithLinks(notice.content, '#1997eb')}
                    </p>

                    {/* Images */}
                    {notice.image_urls && notice.image_urls.length > 0 && (
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
                            {notice.image_urls.map((img, idx) => (
                                <img
                                    key={idx}
                                    src={img.url}
                                    alt={`Notice image ${idx + 1}`}
                                    style={{
                                        width: '120px',
                                        height: '90px',
                                        objectFit: 'cover',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                    }}
                                    onClick={() => setLightboxSrc(img.url)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {/* ------------------------------------------------------------------ */}
            {/* Create / Edit Modal                                                 */}
            {/* ------------------------------------------------------------------ */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.6)',
                    zIndex: 1000,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px',
                }}>
                    <div style={{
                        background: isDark ? '#2a2a2a' : '#fff',
                        borderRadius: '12px',
                        padding: '28px',
                        width: '100%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
                    }}>
                        <h3 style={{ color: textColor, marginTop: 0, marginBottom: '20px' }}>
                            {editingNotice ? 'Edit Notice' : 'Post New Notice'}
                        </h3>

                        {/* Title */}
                        <label style={labelStyle}>Title *</label>
                        <input
                            type="text"
                            style={inputStyle}
                            value={formTitle}
                            onChange={(e) => setFormTitle(e.target.value)}
                            placeholder="Notice title..."
                        />

                        {/* Content */}
                        <label style={labelStyle}>Content *</label>
                        <textarea
                            style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
                            value={formContent}
                            onChange={(e) => setFormContent(e.target.value)}
                            placeholder="Write your notice here..."
                        />

                        {/* Active toggle (edit only) */}
                        {editingNotice && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                                <input
                                    type="checkbox"
                                    id="notice-active"
                                    checked={formActive}
                                    onChange={(e) => setFormActive(e.target.checked)}
                                />
                                <label htmlFor="notice-active" style={{ color: textColor, fontSize: '14px', cursor: 'pointer' }}>
                                    Active (visible to drivers)
                                </label>
                            </div>
                        )}

                        {/* Images */}
                        <label style={labelStyle}>
                            Images — PNG only, max {MAX_IMAGES}
                        </label>

                        {/* Existing images */}
                        {formImages.length > 0 && (
                            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                {formImages.map((img, idx) => (
                                    <div key={idx} style={{ position: 'relative' }}>
                                        <img
                                            src={img.url}
                                            alt={`Upload ${idx + 1}`}
                                            style={{
                                                width: '90px', height: '70px',
                                                objectFit: 'cover', borderRadius: '6px',
                                                border: `1px solid ${isDark ? '#555' : '#ddd'}`,
                                            }}
                                        />
                                        <button
                                            onClick={() => handleRemoveImage(idx)}
                                            style={{
                                                position: 'absolute', top: '-6px', right: '-6px',
                                                background: '#dc3545', color: '#fff',
                                                border: 'none', borderRadius: '50%',
                                                width: '20px', height: '20px',
                                                fontSize: '12px', cursor: 'pointer',
                                                lineHeight: '20px', textAlign: 'center',
                                                padding: 0,
                                            }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Upload button */}
                        {formImages.length < MAX_IMAGES && (
                            <div style={{ marginBottom: '16px' }}>
                                <label style={{
                                    display: 'inline-block',
                                    padding: '8px 16px',
                                    background: imageUploading ? '#999' : (isDark ? '#555' : '#e0e0e0'),
                                    color: textColor,
                                    borderRadius: '6px',
                                    cursor: imageUploading ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                }}>
                                    {imageUploading ? 'Uploading...' : '+ Add Image'}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUpload}
                                        disabled={imageUploading}
                                        multiple
                                    />
                                </label>
                                <span style={{ marginLeft: '10px', fontSize: '12px', color: subText }}>
                                    {MAX_IMAGES - formImages.length} slot{MAX_IMAGES - formImages.length !== 1 ? 's' : ''} remaining
                                </span>
                            </div>
                        )}

                        {/* Error */}
                        {formError && (
                            <div style={{ background: '#f8d7da', border: '1px solid #f5c6cb', padding: '10px 14px', borderRadius: '6px', color: '#721c24', marginBottom: '16px', fontSize: '13px' }}>
                                {formError}
                            </div>
                        )}

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <button style={btnSecondary} onClick={closeModal} disabled={formSubmitting}>
                                Cancel
                            </button>
                            <button
                                style={{ ...btnPrimary, opacity: formSubmitting || imageUploading ? 0.7 : 1 }}
                                onClick={handleSubmit}
                                disabled={formSubmitting || imageUploading}
                            >
                                {formSubmitting ? 'Saving...' : editingNotice ? 'Save Changes' : 'Post Notice'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ------------------------------------------------------------------ */}
            {/* Lightbox                                                            */}
            {/* ------------------------------------------------------------------ */}
            {lightboxSrc && (
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.85)',
                        zIndex: 2000,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'zoom-out',
                    }}
                    onClick={() => setLightboxSrc(null)}
                >
                    <img
                        src={lightboxSrc}
                        alt="Full size"
                        style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
                    />
                </div>
            )}
        </div>
    );
};

export default NoticeBoard;
